/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Clone User Modal (super admin)
 * Collects the new identity (first/last name, email, password) required to
 * deep-clone a user, then POSTs to /api/admin/users/:id/clone.
 */

import { useState, useEffect } from 'react'
import { Copy, Eye, EyeOff, Info } from 'lucide-react'
import toast from 'react-hot-toast'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const initialForm = { firstName: '', lastName: '', email: '', password: '' }

const CloneUserModal = ({ isOpen, user, onClose, onCloned }) => {
  const [form, setForm] = useState(initialForm)
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Reset the form whenever the modal opens for a (new) user
  useEffect(() => {
    if (isOpen) {
      setForm(initialForm)
      setShowPassword(false)
    }
  }, [isOpen, user?.id])

  if (!isOpen || !user) return null

  const emailValid = EMAIL_REGEX.test(form.email.trim())
  const passwordValid = form.password.length >= 6
  const canSubmit =
    form.firstName.trim() && form.lastName.trim() && emailValid && passwordValid && !submitting

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }))

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const response = await fetch(`/api/admin/users/${user.id}/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          password: form.password
        })
      })
      const data = await response.json()
      if (response.ok) {
        toast.success(`User cloned successfully as ${data.user?.email || form.email.trim()}`)
        onCloned?.(data.user)
        onClose()
      } else {
        toast.error(data.error || 'Failed to clone user')
      }
    } catch {
      toast.error('Failed to clone user')
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <Copy className="w-5 h-5 text-indigo-500 mr-2" />
            Clone User
          </h3>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            You are about to clone <strong>{user.email}</strong>. Enter the new account&apos;s name,
            email, and password to continue.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First name
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={setField('firstName')}
                placeholder="First name"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last name
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={setField('lastName')}
                placeholder="Last name"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={setField('email')}
              placeholder="newuser@example.com"
              className={inputClass}
            />
            {form.email && !emailValid && (
              <p className="text-xs text-red-500 mt-1">Enter a valid email address</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={setField('password')}
                placeholder="At least 6 characters"
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {form.password && !passwordValid && (
              <p className="text-xs text-red-500 mt-1">Password must be at least 6 characters</p>
            )}
          </div>

          <div className="flex items-start text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
            <Info className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
            <span>
              The clone copies the user&apos;s full learning history (challenges, responses, levels,
              score reports, badges). It is created as a mock user with no admin rights, no
              sessions, and no coach/guardian/organization relationships. The public name and avatar
              are regenerated.
            </span>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-3 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Cloning…' : 'Clone User'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CloneUserModal
